import { gql, useLazyQuery, useMutation } from '@apollo/client';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import type { Institution } from '@interfaces/transfer';
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  type SxProps,
} from '@mui/material';
import type { CountryCodes } from '@onevesthq/ov-enums';
import { AddressField } from '@ovComponents/2-component/addressField/addressField';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { generateAddressString } from '@utils/common';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Item = Required<{
  id: string;
  name: string;
  physicalAddress: PhysicalAddress;
}>;
type SearchResults = { items: Item[]; count: number };

export const FETCH_INSTITUTIONS = gql`
  query fetchInstitutions($input: FetchInstitutionsInput!) {
    fetchInstitutions(input: $input) {
      totalCount
      institutions {
        id
        isDraft
        name
        physicalAddress {
          city
          province
          streetName
          postal
          unitNumber
          houseNumber
          neighborhood
          jurisdiction
          country
        }
      }
    }
  }
`;

export const CREATE_INSTITUTION = gql`
  mutation createInstitution($input: CreateInstitutionInput!) {
    createInstitution(input: $input) {
      institution {
        id
        name
      }
    }
  }
`;

export const BankSelectField = ({
  setInstitution,
  label,
  disabled,
  fullWidth,
  labelSx,
  sx,
  onBlur,
  error,
  addInstitutionManually,
  originOfAccount,
}: {
  institution: any;
  setInstitution: (institution: Institution | undefined) => void;
  label: string;
  disabled?: boolean;
  onBlur?: any;
  error?: any;
  fullWidth?: boolean;
  isJointAccount?: boolean;
  labelSx?: SxProps;
  sx?: SxProps;
  addInstitutionManually?: boolean;
  originOfAccount?: CountryCodes;
}) => {
  const [searchResult, setSearchResult] = useState<SearchResults>({
    items: [],
    count: 0,
  });
  const { t } = useTranslation('components');
  const [institutionName, setInstitutionName] = useState('');
  const [selectedInstitutionName, setSelectedInstitutionName] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [searchInstitutions, { refetch }] = useLazyQuery(FETCH_INSTITUTIONS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const [openCreateInstitutionDialog, setOpenCreateInstitutionDialog] =
    useState(false);
  const emptyAddress = {
    city: '',
    country: undefined,
    houseNumber: '',
    neighborhood: '',
    postal: '',
    province: '',
    streetName: '',
    unitNumber: '',
  };
  const [physicalAddress, setPhysicalAddress] =
    useState<PhysicalAddress>(emptyAddress);

  useEffect(() => {
    if (originOfAccount) {
      searchFn('');
    }
  }, [originOfAccount]);

  const [createInstitution, options] = useMutation(CREATE_INSTITUTION, {
    variables: {
      input: {
        isDraft: true,
        name: institutionName,
        physicalAddress,
      },
    },
  });
  const searchFn = (query?: string): Promise<SearchResults> =>
    new Promise<SearchResults>(() => {
      searchInstitutions({
        variables: {
          input: {
            filter: {
              searchText: query && query !== '' ? query : undefined,
              countries: originOfAccount ? [originOfAccount] : undefined,
            },
          },
        },
        onCompleted: (data) => {
          setSearchResult({
            items: [...data.fetchInstitutions.institutions],
            count: Number(data.fetchInstitutions.totalCount),
          });
        },
        onError: () => {
          setSearchResult({
            items: [],
            count: 0,
          });
        },
      }).then();
    });

  useEffect(() => {
    searchFn('');
  }, []);

  useEffect(() => {
    searchFn(selectedInstitutionName);
  }, [selectedId]);

  const searchOptions = [
    ...searchResult.items.map((item) => ({
      label: `${item.name} - ${generateAddressString(item.physicalAddress)}`,
      id: item.id,
    })),
    ...(addInstitutionManually
      ? [
          {
            label: t(
              'components:externalInstitutionSelect.selectInstitutionDialog.addInstitution',
            ),
            id: 'manual',
          },
        ]
      : []),
  ];

  return (
    <>
      <Autocomplete
        data-testid='userSelectField'
        freeSolo
        disabled={disabled}
        options={searchOptions}
        fullWidth={fullWidth}
        label={label}
        isOptionEqualToValue={(option: any, value: any) =>
          option.label === value
        }
        onInputChange={(_e: any, value: string) =>
          value !== '+ Add new institution' && searchFn(value)
        }
        onBlur={onBlur}
        error={error}
        onChange={(_e: any, value: any) => {
          if (value?.id === 'manual') {
            setOpenCreateInstitutionDialog(true);
          } else if (value?.id) {
            const result = searchResult.items.find(
              (item) => item.id === value.id,
            );
            setInstitution(result as Institution);
          }
        }}
        sx={sx}
        renderTags={(tagValue: any, getTagProps: any) =>
          tagValue.map((option: any, index: number) => (
            <Chip
              label={option}
              sx={{ marginTop: '-2px !important' }}
              {...getTagProps({ index })}
              size='small'
              onDelete={undefined}
            />
          ))
        }
        labelSx={labelSx}
      />
      <Dialog
        onClose={() => setOpenCreateInstitutionDialog(false)}
        open={openCreateInstitutionDialog}
        fullWidth
      >
        <DialogTitle>
          {t('components:externalInstitutionSelect.addInstitutionDialog.title')}
        </DialogTitle>
        <DialogContent>
          <TextField
            value={institutionName}
            label={t(
              'components:externalInstitutionSelect.addInstitutionDialog.institutionNameLabel',
            )}
            fullWidth
            onChange={(e: any) => setInstitutionName(e.target.value)}
            data-testid='institution-name'
            sx={{ marginBottom: '20px', marginTop: '10px' }}
          />
          <AddressField
            address={physicalAddress}
            label='Address'
            onChange={(e) => setPhysicalAddress(e)}
          />
          <Button
            data-testid='updateInstitutionButton'
            variant='contained'
            fullWidth
            size='medium'
            disabled={options.loading}
            style={{ marginTop: '20px' }}
            onClick={async () => {
              setOpenCreateInstitutionDialog(false);
              const response = await createInstitution();
              if (response.data) {
                setSelectedId(response.data.createInstitution.institution.id);
                setInstitution(response.data.createInstitution.institution);
                setSelectedInstitutionName(institutionName);
                refetch();
                setInstitutionName('');
                setPhysicalAddress(emptyAddress);
              }
            }}
          >
            {t('components:externalInstitutionSelect.confirmButton')}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
